// buildSearchDateRange — pure computation of the FlightAware search date window.
//
// Extracted verbatim from `FlightLookup.js` (module-level `buildSearchDateRange`)
// as part of the behavior-preserving refactor (issue #240). Given the form's
// selected date string it returns `{ startDate, endDate }` as `YYYY-MM-DD`
// strings spanning the selected day plus one (to catch flights crossing UTC
// dates), capped at today + 2 days because the FlightAware API rejects requests
// further into the future.
//
// The source read the wall-clock via `new Date()` twice — once as the fallback
// when no date was supplied, and once to compute the today+2 cap. To keep the
// function pure those reads are injected through the `now` parameter (defaulting
// to `new Date()` so the call site behavior is identical). `now` is cloned with
// `new Date(now)` before mutation so the caller's value is never altered.
//
// React-free and side-effect-free.
//
// Validates: Requirements 3.1, 3.4, 3.5, 1.6

import formatDateStr from './formatDateStr.js';

/**
 * @typedef {Object} SearchDateRange
 * @property {string} startDate - `YYYY-MM-DD` start of the search window.
 * @property {string} endDate - `YYYY-MM-DD` end of the search window (capped at now+2).
 */

/**
 * Builds the start/end date range for the FlightAware API search.
 *
 * @param {string|null|undefined} dateStr - The form's selected date (`YYYY-MM-DD`), or falsy for today.
 * @param {Date} [now=new Date()] - The reference "now" used for the fallback date and the today+2 cap.
 * @returns {SearchDateRange} The `{ startDate, endDate }` window as `YYYY-MM-DD` strings.
 */
export function buildSearchDateRange(dateStr, now = new Date()) {
  // Use local date instead of UTC date for search
  const searchDate = dateStr ? new Date(dateStr + 'T00:00:00') : new Date(now);
  const startDate = formatDateStr(searchDate);

  // Search 1 day ahead to catch flights spanning UTC dates.
  // Cap at today + 2 days — FlightAware API rejects requests further into the future.
  const maxEndDate = new Date(now);
  maxEndDate.setDate(maxEndDate.getDate() + 2);

  const endDateObj = new Date(searchDate);
  endDateObj.setDate(endDateObj.getDate() + 1);

  const cappedEnd = endDateObj < maxEndDate ? endDateObj : maxEndDate;
  const endDate = formatDateStr(cappedEnd);

  return { startDate, endDate };
}

export default buildSearchDateRange;
